import { StrictMode } from 'react'
import './i18n'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

// ─────────────────────────────────────────────────────────────────────────────
// GHL WIDGET NAVIGATION GUARD — installed SYNCHRONOUSLY before React mounts.
//
// Strategy: lock the page to its current pathname FOREVER.
// Any attempt to change the pathname (pushState, replaceState, location.href,
// location.assign, location.replace, hashchange, popstate) is silently dropped.
//
// The * wildcard route has also been removed from the router, so even if
// something slips through, React Router won't re-render for unknown paths.
// ─────────────────────────────────────────────────────────────────────────────
(function installGHLGuard() {
  // Capture the ONE valid pathname at startup — never allow it to change.
  const LOCKED_PATH = window.location.pathname;

  const originalPush    = window.history.pushState.bind(window.history);
  const originalReplace = window.history.replaceState.bind(window.history);

  // Returns true if this URL would change the pathname away from LOCKED_PATH.
  const wouldChangePath = (url: string | URL | null | undefined): boolean => {
    if (url == null) return false;
    try {
      const target = new URL(String(url), window.location.href);
      return target.pathname !== LOCKED_PATH;
    } catch {
      // Relative URL like "chat" — treat as a path change, block it.
      return true;
    }
  };

  // Intercept pushState
  window.history.pushState = (state, unused, url) => {
    if (wouldChangePath(url)) return;
    originalPush(state, unused, url);
  };

  // Intercept replaceState
  window.history.replaceState = (state, unused, url) => {
    if (wouldChangePath(url)) return;
    originalReplace(state, unused, url);
  };

  // Intercept popstate (back/forward button)
  window.addEventListener('popstate', (e: PopStateEvent) => {
    // Always restore the locked path — don't let back/forward navigate away
    if (window.location.pathname !== LOCKED_PATH) {
      e.stopImmediatePropagation();
      originalPush(null, '', LOCKED_PATH);
    }
  }, true);

  // Intercept hashchange — GHL sometimes uses hash routing
  window.addEventListener('hashchange', (e: HashChangeEvent) => {
    try {
      const newPath = new URL(e.newURL).pathname;
      if (newPath !== LOCKED_PATH) {
        e.stopImmediatePropagation();
        originalPush(null, '', LOCKED_PATH);
      }
    } catch {
      // ignore
    }
  }, true);

  // Intercept window.location.href = "..." and .assign() / .replace()
  // These bypass history API entirely and cause a full navigation.
  // We override them to be no-ops when they would change the pathname.
  try {
    const locProto = window.Location.prototype;

    const origAssign  = locProto.assign.bind(window.location);
    const origReplace = locProto.replace.bind(window.location);

    locProto.assign = function(url: string) {
      try {
        const target = new URL(url, window.location.href);
        if (target.pathname !== LOCKED_PATH) return;
      } catch { return; }
      origAssign(url);
    };

    locProto.replace = function(url: string) {
      try {
        const target = new URL(url, window.location.href);
        if (target.pathname !== LOCKED_PATH) return;
      } catch { return; }
      origReplace(url);
    };
  } catch {
    // Location.prototype override not supported — skip
  }

  // Block postMessage navigation payloads from GHL iframe origins
  window.addEventListener('message', (e: MessageEvent) => {
    const origin = e.origin || '';
    const isGHL =
      origin.includes('leadconnectorhq.com') ||
      origin.includes('msgsndr.com') ||
      origin.includes('gohighlevel.com');
    if (!isGHL) return;
    try {
      const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      if (
        data &&
        (data.type === 'navigate' ||
          data.type === 'route' ||
          data.type === 'redirect' ||
          data.action === 'navigate' ||
          data.action === 'route' ||
          typeof data.url === 'string' ||
          typeof data.path === 'string' ||
          typeof data.href === 'string')
      ) {
        e.stopImmediatePropagation();
      }
    } catch {
      // not JSON — ignore
    }
  }, true);
})();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)